import { useState, useEffect, useCallback, useRef } from "react";

export interface AnimationFrame<T = unknown> {
  data: T;
  durationMs: number;
}

interface UseFrameAnimationOptions {
  autoStart?: boolean;
  loop?: boolean;
  onComplete?: () => void;
}

/**
 * Drives a frame-based animation sequence with configurable durations per frame.
 * Generic over the frame payload so callers can attach any data (pose info, image refs, etc.).
 */
export function useFrameAnimation<T>(
  frames: AnimationFrame<T>[],
  options: UseFrameAnimationOptions = {},
) {
  const { autoStart = false, loop = false, onComplete } = options;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const prevFramesRef = useRef(frames);

  // Auto-start on initial non-empty frames, and restart when the frames array changes
  useEffect(() => {
    const framesChanged = frames !== prevFramesRef.current;
    prevFramesRef.current = frames;

    if (autoStart && frames.length > 0) {
      if (framesChanged || (!isPlaying && !isDone)) {
        setCurrentIndex(0);
        setIsDone(false);
        setIsPlaying(true);
      }
    }
    if (frames.length === 0) {
      setIsPlaying(false);
      setIsDone(false);
      setCurrentIndex(0);
    }
  }, [autoStart, frames]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = useCallback(() => {
    setCurrentIndex(0);
    setIsDone(false);
    setIsPlaying(true);
  }, []);

  const stop = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const reset = useCallback(() => {
    setCurrentIndex(0);
    setIsDone(false);
    setIsPlaying(false);
  }, []);

  useEffect(() => {
    if (!isPlaying || frames.length === 0) return;

    const frame = frames[currentIndex];
    if (!frame) {
      setIsPlaying(false);
      setIsDone(true);
      onCompleteRef.current?.();
      return;
    }

    const timeout = setTimeout(() => {
      const nextIndex = currentIndex + 1;
      if (nextIndex < frames.length) {
        setCurrentIndex(nextIndex);
      } else if (loop) {
        setCurrentIndex(0);
      } else {
        setIsPlaying(false);
        setIsDone(true);
        onCompleteRef.current?.();
      }
    }, frame.durationMs);

    return () => clearTimeout(timeout);
  }, [isPlaying, currentIndex, frames, loop]);

  return {
    currentFrame: frames[currentIndex] ?? null,
    currentIndex,
    isPlaying,
    isDone,
    start,
    stop,
    reset,
  };
}
